import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import api from '@services/api';

const NotificationContext = createContext(null);

// Poll interval: 5 minutes
const POLL_INTERVAL = 5 * 60 * 1000;

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const pollIntervalRef = useRef(null);
  const lastNotificationId = useRef(null);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async (showLoading = false) => {
    if (!isAuthenticated) return;

    try {
      if (showLoading) setLoading(true);

      // Fetch recent notifications
      const [notificationsRes, countRes] = await Promise.all([
        api.get('/common/user-notifications/recent/', { params: { limit: 20 } }),
        api.get('/common/user-notifications/unread_count/')
      ]);

      const newNotifications = notificationsRes.data || [];
      setNotifications(newNotifications);
      setUnreadCount(countRes.data?.count || 0);

      // Check for new notifications and show browser notification
      if (newNotifications.length > 0 && lastNotificationId.current) {
        const latestId = newNotifications[0]?.id;
        if (latestId && latestId !== lastNotificationId.current) {
          // New notification arrived
          const newOnes = newNotifications.filter(n => !n.is_read && n.id > lastNotificationId.current);
          newOnes.forEach(notification => {
            showBrowserNotification(notification);
          });
        }
      }

      if (newNotifications.length > 0) {
        lastNotificationId.current = newNotifications[0]?.id;
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Show browser notification
  const showBrowserNotification = useCallback((notification) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(notification.title || 'New Notification', {
        body: notification.message || '',
        icon: '/favicon.ico',
        tag: `notification-${notification.id}`,
      });
    }
  }, []);

  // Mark a notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await api.post(`/common/user-notifications/${notificationId}/mark_read/`);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, is_read: true } : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await api.post('/common/user-notifications/mark_all_read/');
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, []);

  // Refresh unread count
  const refreshUnreadCount = useCallback(async () => {
    try {
      const response = await api.get('/common/user-notifications/unread_count/');
      setUnreadCount(response.data?.count || 0);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, []);

  // Request browser notification permission
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return Notification.permission === 'granted';
  }, []);

  // Start polling when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Initial fetch
      fetchNotifications(true);
      requestNotificationPermission();

      // Set up polling interval
      pollIntervalRef.current = setInterval(() => {
        fetchNotifications(false);
      }, POLL_INTERVAL);
    } else {
      // Clear notifications when logged out
      setNotifications([]);
      setUnreadCount(0);
      lastNotificationId.current = null;
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isAuthenticated, fetchNotifications, requestNotificationPermission]);

  const value = {
    notifications,
    unreadCount,
    loading,
    connected: isAuthenticated, // For compatibility
    markAsRead,
    markAllAsRead,
    refreshUnreadCount,
    refreshNotifications: () => fetchNotifications(true),
    requestNotificationPermission,
    setNotifications,
    setUnreadCount,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
